"use client";

import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { FileText, Check, Loader2, Library, ArrowRight } from "lucide-react";

/* ── Fake data ─────────────────────────────────── */

const SOURCES = ["Royal Road", "Wattpad", "Substack", ".docx / .md"];

const CHAPTERS = [
  "Prologue — The Last Ember",
  "Chapter 1 — Ashes",
  "Chapter 2 — Storm-Grey Eyes",
  "Chapter 3 — The Oath",
  "Chapter 4 — Rock Haven",
];

/* ── Component ─────────────────────────────────── */

export function ImportDemo() {
  const [step, setStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    async function runAnimation() {
      const isCancelled = () => cancelledRef.current;

      setStep(0);
      await wait(500);
      if (isCancelled()) return;

      // Step 1: source chips + files appear
      setStep(1);
      await wait(1200);
      if (isCancelled()) return;

      // Step 2: importing — progress bar fills, rows stream in
      setStep(2);
      await wait(2200);
      if (isCancelled()) return;

      // Step 3: rows flip to "imported" checks
      setStep(3);
      await wait(1600);
      if (isCancelled()) return;

      // Step 4: success banner
      setStep(4);
      await wait(5000);
      if (isCancelled()) return;

      if (!isCancelled()) runAnimation();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.4 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      cancelledRef.current = true;
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-12 px-6 pt-24 pb-12 md:flex-row lg:px-12"
    >
      {/* ── Visual Demo Container ──────────────── */}
      <div className="relative w-full max-w-2xl flex-1 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-900/20 via-background to-purple-900/10 p-4 shadow-2xl before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/10 before:via-transparent before:to-transparent">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />

        <div className="relative overflow-hidden rounded-2xl border border-border bg-card/90 shadow-inner backdrop-blur-xl">
          {/* Panel header */}
          <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-3">
            <Library className="size-4 text-brand" />
            <span className="text-sm font-bold text-foreground">
              Import your library
            </span>
            <span
              className={cn(
                "ml-auto flex items-center gap-1.5 text-xs font-medium transition-opacity duration-500",
                step >= 2 && step < 4
                  ? "text-brand opacity-100"
                  : "opacity-0"
              )}
            >
              <Loader2 className="size-3.5 animate-spin" />
              Importing…
            </span>
          </div>

          <div className="space-y-4 p-5">
            {/* Source chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                From
              </span>
              {SOURCES.map((src, i) => (
                <span
                  key={src}
                  className={cn(
                    "rounded-full border border-border bg-background/70 px-2.5 py-1 text-[11px] font-semibold text-foreground/80 transition-all duration-500",
                    step >= 1
                      ? "translate-y-0 opacity-100"
                      : "translate-y-2 opacity-0"
                  )}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  {src}
                </span>
              ))}
            </div>

            {/* Progress bar */}
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-[width] ease-out",
                  step >= 2 ? "w-full duration-[2000ms]" : "w-0 duration-0"
                )}
              />
            </div>

            {/* Chapter rows */}
            <div className="space-y-2">
              {CHAPTERS.map((title, i) => (
                <div
                  key={title}
                  className={cn(
                    "flex items-center gap-3 rounded-xl border border-border/60 bg-background/60 px-3 py-2.5 transition-all duration-500",
                    step >= 2
                      ? "translate-x-0 opacity-100"
                      : "-translate-x-4 opacity-0"
                  )}
                  style={{ transitionDelay: `${i * 160}ms` }}
                >
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-xs font-medium text-foreground">
                    {title}
                  </span>
                  {/* Status: spinner → check */}
                  <span className="shrink-0">
                    {step >= 3 ? (
                      <span
                        className="flex size-5 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500 transition-all duration-300"
                        style={{ transitionDelay: `${i * 120}ms` }}
                      >
                        <Check className="size-3.5" strokeWidth={3} />
                      </span>
                    ) : (
                      <Loader2 className="size-4 animate-spin text-muted-foreground/60" />
                    )}
                  </span>
                </div>
              ))}

              {/* "and more" row to imply a big backlog */}
              <div
                className={cn(
                  "px-3 text-[11px] font-medium text-muted-foreground transition-opacity duration-500",
                  step >= 2 ? "opacity-100" : "opacity-0"
                )}
                style={{ transitionDelay: "820ms" }}
              >
                + 7 more chapters
              </div>
            </div>

            {/* Success banner */}
            <div
              className={cn(
                "flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 transition-all duration-500",
                step >= 4
                  ? "translate-y-0 opacity-100"
                  : "pointer-events-none translate-y-2 opacity-0"
              )}
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-500">
                <Check className="size-4" strokeWidth={3} />
              </span>
              <div className="text-sm">
                <span className="font-bold text-foreground">
                  12 chapters imported
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  — ready to publish.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Text Explainer ─────────────────────── */}
      <div className="max-w-lg flex-1 space-y-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
          <ArrowRight className="size-3.5" />
          Switching platforms
        </div>
        <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          Bring your whole library with you
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Already publishing on Royal Road, Wattpad, or Substack? Import your
          existing chapters in a few clicks — drop in your files and every
          chapter lands formatted and ready to publish.
        </p>

        <ul className="mt-8 space-y-4">
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 1 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: "400ms" }}
          >
            <div className="mt-1 shrink-0 rounded-full bg-indigo-500/20 p-1 text-indigo-400">
              <Check className="size-4" strokeWidth={3} />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                From anywhere:
              </span>{" "}
              Bring over Markdown or Word files — or paste chapters straight in.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 2 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: "600ms" }}
          >
            <div className="mt-1 shrink-0 rounded-full bg-purple-500/20 p-1 text-purple-400">
              <Check className="size-4" strokeWidth={3} />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Formatting preserved:
              </span>{" "}
              Bold, italics, scene breaks and chapter titles all carry over.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 3 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: "800ms" }}
          >
            <div className="mt-1 shrink-0 rounded-full bg-emerald-500/20 p-1 text-emerald-400">
              <Check className="size-4" strokeWidth={3} />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Big backlogs welcome:
              </span>{" "}
              Import hundreds of chapters at once — we handle it in the
              background.
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}
